/**
 * LabelCanvas Component
 *
 * Canvas container for the label layout editor.
 * Displays label outline at actual dimensions * scale factor.
 * CSS grid background for alignment reference.
 *
 * Features:
 * - Responsive overflow handling
 * - CSS grid background (performant, no canvas/SVG)
 * - Dimension labels for reference
 * - Touch-friendly padding
 * - ARIA attributes for accessibility
 *
 * IMPORTANT: Canvas surface has position: relative (REQUIRED for react-rnd bounds="parent")
 */

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLabelLayoutStore } from './label-layout-store';
import { PX_PER_MM } from './label-layout-types';
import { cn } from '@/lib/utils';

// ============================================================================
// LABEL CANVAS
// ============================================================================

interface LabelCanvasProps {
  children: ReactNode;
  className?: string;
  showDimensionLabels?: boolean;
}

/**
 * Canvas container for the label layout editor.
 * Renders at actual mm dimensions scaled for comfortable editing.
 */
export function LabelCanvas({
  children,
  className,
  showDimensionLabels = true,
}: LabelCanvasProps) {
  const { t } = useTranslation('settings');
  const canvasSize = useLabelLayoutStore((s) => s.canvasSize);
  const { scale, showGrid, gridSizeMm } = useLabelLayoutStore((s) => s.editor);

  // Convert mm to screen pixels at current scale
  const widthPx = canvasSize.width * PX_PER_MM * scale;
  const heightPx = canvasSize.height * PX_PER_MM * scale;
  const gridSizePx = gridSizeMm * PX_PER_MM * scale;

  return (
    <div
      className={cn(
        'flex items-center justify-center p-4 sm:p-6 lg:p-8',
        'bg-muted/30 rounded-xl overflow-auto',
        'min-h-[300px]', // Minimum height for small canvases
        className
      )}
      role="region"
      aria-label={t('production.canvas.ariaLabel')}
    >
      <div className="relative">
        {/* Dimension labels */}
        {showDimensionLabels && (
          <>
            <div
              className="absolute -top-6 left-0 right-0 text-center text-xs text-muted-foreground font-medium"
              aria-hidden="true"
            >
              <span className="bg-muted/50 px-2 py-0.5 rounded-full">
                {canvasSize.width}mm
              </span>
            </div>
            <div
              className="absolute -left-8 top-0 bottom-0 flex items-center text-xs text-muted-foreground font-medium"
              style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}
              aria-hidden="true"
            >
              <span className="bg-muted/50 px-2 py-0.5 rounded-full">
                {canvasSize.height}mm
              </span>
            </div>
          </>
        )}

        {/* Canvas surface - position: relative required for react-rnd bounds */}
        <div
          className={cn(
            'relative border-2 shadow-lg transition-shadow',
            'border-border bg-background',
            'hover:shadow-xl'
          )}
          style={{
            width: widthPx,
            height: heightPx,
            // Grid background when enabled - uses CSS variables for theme awareness
            backgroundImage: showGrid
              ? `
                linear-gradient(to right, hsl(var(--border) / 0.5) 1px, transparent 1px),
                linear-gradient(to bottom, hsl(var(--border) / 0.5) 1px, transparent 1px)
              `
              : 'none',
            backgroundSize: showGrid
              ? `${gridSizePx}px ${gridSizePx}px`
              : undefined,
            // Align grid to (0,0) coordinate origin. CSS backgrounds start at padding-box
            // (inside border), but we want grid lines at canvas coordinates, so we offset
            // by -1px (half of the 2px border) to align the first grid line with the edge.
            backgroundPosition: showGrid ? '-1px -1px' : undefined,
          }}
          data-testid="label-canvas"
        >
          {/* Components render here */}
          {children}
        </div>

        {/* Scale indicator */}
        <div
          className="absolute -bottom-6 right-0 text-xs text-muted-foreground"
          aria-hidden="true"
        >
          {Math.round(scale * 100)}%
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// EMPTY STATE
// ============================================================================

/**
 * Empty state displayed when canvas has no components.
 */
export function LabelCanvasEmptyState() {
  const { t } = useTranslation('settings');
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground p-4">
      <svg
        className="h-12 w-12 mb-3 opacity-40"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M4 5a1 1 0 011-1h14a1 1 0 011 1v14a1 1 0 01-1 1H5a1 1 0 01-1-1V5z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M9 9h6M9 12h6M9 15h4"
        />
      </svg>
      <p className="text-sm font-medium">{t('production.canvas.noComponentsTitle')}</p>
      <p className="text-xs mt-1">{t('production.canvas.noComponentsDesc')}</p>
    </div>
  );
}

// ============================================================================
// LOADING SKELETON
// ============================================================================

/**
 * Loading skeleton displayed while fetching template data.
 */
export function LabelCanvasSkeleton() {
  return (
    <div className="flex items-center justify-center p-8 bg-muted/30 rounded-xl">
      <div className="animate-pulse">
        <div className="bg-muted rounded-lg" style={{ width: 300, height: 200 }} />
      </div>
    </div>
  );
}
